import { useState, type Dispatch, type SetStateAction } from 'react';
import type { SolicitudCotizacion } from '@laser/core';
import { useCatalogo, useParametros, useRecotizar } from '../../api/hooks';
import type { TrabajoDetalle } from '../../api/tipos';
import { Campo, EntradaNumero, mensajeError, useAvisar } from '../../componentes/ui';
import { conEscalas, escalaDeLinea } from '../../lib/escala';
import { pesos } from '../../lib/formato';
import { ControlesEscala } from './ControlesEscala';

export function Recotizar({ trabajo, borrador, setBorrador, cambios, onDeshacer, onLinea }: {
  trabajo: TrabajoDetalle; borrador: SolicitudCotizacion; setBorrador: Dispatch<SetStateAction<SolicitudCotizacion | null>>;
  cambios: boolean; onDeshacer: () => void;
  onLinea: (archivoId: string, c: { cantidad?: number; escalaX?: number; escalaY?: number }) => void;
}) {
  const cat = useCatalogo();
  const parametros = useParametros();
  const recotizar = useRecotizar(trabajo.id);
  const avisar = useAvisar();
  const [error, setError] = useState<string | null>(null);
  const set = (c: Partial<SolicitudCotizacion>) => setBorrador((b) => b && { ...b, ...c });
  const puede = (maquinaId: string) => (parametros.data ?? []).some((p) => p.maquinaId === maquinaId && p.materialId === borrador.materialId);
  const archivo = (archivoId: string) => trabajo.archivos.find((a) => a.id === archivoId);
  const nombre = (archivoId: string) => archivo(archivoId)?.nombreOriginal ?? archivoId;

  const enviar = () => {
    setError(null);
    // La solicitud siempre viaja con escalaX/escalaY explícitas.
    recotizar.mutate(conEscalas(borrador), {
      onSuccess: (c) => avisar(`Nueva cotización: ${pesos(c.totalCentavos)}`, 'ok'),
      onError: (e) => setError(mensajeError(e)),
    });
  };

  return (
    <section className="tarjeta" aria-labelledby="recot-t">
      <div className="fila fila--entre">
        <h2 id="recot-t">Recotizar</h2>
        {cambios && <span className="pendiente">Cambios sin recotizar</span>}
      </div>
      <div className="campos">
        <Campo etiqueta="Material" htmlFor="r-material">
          <select id="r-material" value={borrador.materialId} onChange={(e) => set({ materialId: e.target.value })}>
            {cat.materiales.filter((m) => m.activo || m.id === borrador.materialId).map((m) => (
              <option key={m.id} value={m.id}>{m.nombre} {m.espesorMm} mm{m.esPlaceholder ? ' (por confirmar)' : ''}</option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Máquina" htmlFor="r-maquina">
          <select id="r-maquina" value={borrador.maquinaId ?? ''} onChange={(e) => set({ maquinaId: e.target.value || undefined })}>
            <option value="">Automática (la más barata)</option>
            {cat.maquinas.map((m) => (
              <option key={m.id} value={m.id} disabled={!puede(m.id)}>
                {m.nombre}{!puede(m.id) ? ' — sin parámetros' : !m.activa ? ' (inactiva)' : ''}
              </option>
            ))}
          </select>
        </Campo>
        <div className="campo" style={{ justifyContent: 'end', gap: 8 }}>
          <label className="check"><input type="checkbox" checked={borrador.materialDelCliente} onChange={(e) => set({ materialDelCliente: e.target.checked })} /> Material del cliente</label>
          <label className="check"><input type="checkbox" checked={borrador.urgente} onChange={(e) => set({ urgente: e.target.checked })} /> Urgente</label>
        </div>
      </div>

      <div className="h3" style={{ marginTop: 14 }}>Cantidades y escala</div>
      {borrador.lineas.map((l) => {
        const an = archivo(l.archivoId)?.analisis ?? null;
        return (
          <div className="linea-escala" key={l.archivoId}>
            <div className="linea">
              <span style={{ wordBreak: 'break-all' }}>{nombre(l.archivoId)}</span>
              <span style={{ width: 110 }}>
                <EntradaNumero valor={l.cantidad} min={1} sufijo="pz" aria-label={`Cantidad de ${nombre(l.archivoId)}`}
                  onCambio={(n) => onLinea(l.archivoId, { cantidad: Math.max(1, Math.round(n)) })} />
              </span>
            </div>
            <ControlesEscala nombre={nombre(l.archivoId)} editable escala={escalaDeLinea(l)}
              base={an ? { anchoMm: an.anchoMm, altoMm: an.altoMm } : null}
              onCambio={(e) => onLinea(l.archivoId, e)} />
          </div>
        );
      })}
      <p className="meta" style={{ marginTop: 6 }}>La operación de cada capa se cambia arriba, en cada archivo.</p>

      {error && <div className="aviso aviso--bad" style={{ marginTop: 10 }}>{error}</div>}
      <div className="fila" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="btn" onClick={() => { setError(null); onDeshacer(); }} disabled={!cambios || recotizar.isPending}>Deshacer cambios</button>
        <button type="button" className="btn btn--marca" onClick={enviar} disabled={recotizar.isPending}>{recotizar.isPending ? 'Calculando…' : 'Recotizar'}</button>
      </div>
    </section>
  );
}
